/**
 * Les jours de repos de la semaine, selon le pays de l'entreprise (0 =
 * dimanche … 6 = samedi).
 *
 * <p>Le relevé mensuel, les vacances et les heures prévues au contrat
 * comptaient partout du lundi au vendredi. Une entreprise de Riyad ou de
 * Tel-Aviv travaille le dimanche et se repose le vendredi : son relevé
 * montrait le dimanche comme un jour de week-end travaillé et des vendredis
 * « manquants ».
 */

/** Pays dont le week-end n'est pas le samedi et le dimanche. */
export const REPOS_PAR_PAYS = {
  // Vendredi et samedi.
  DZ: [5, 6],
  BH: [5, 6],
  BD: [5, 6],
  EG: [5, 6],
  IQ: [5, 6],
  IL: [5, 6],
  JO: [5, 6],
  KW: [5, 6],
  LY: [5, 6],
  MV: [5, 6],
  OM: [5, 6],
  QA: [5, 6],
  SA: [5, 6],
  SD: [5, 6],
  SY: [5, 6],
  YE: [5, 6],
  PS: [5, 6],
  // Le vendredi seul.
  IR: [5],
  DJ: [5],
  // Jeudi et vendredi.
  AF: [4, 5],
  SO: [4, 5],
  // Le samedi seul.
  NP: [6],
  // Vendredi et dimanche.
  BN: [5, 0],
};

/** Le samedi et le dimanche : la règle de tous les autres pays. */
const PAR_DEFAUT = [6, 0];

let paysCourant = null;

/**
 * Retient le pays de l'entreprise ouverte : les jours ouvrables s'y règlent.
 *
 * @param {string|null} pays code du pays (« CH », « SA »…)
 */
export function definitPaysDeLaSemaine(pays) {
  paysCourant = pays ? String(pays).toUpperCase() : null;
}

/**
 * @param {string|null} [pays] code du pays ; celui de l'entreprise par défaut
 * @returns {number[]} les jours de repos (0 = dimanche … 6 = samedi)
 */
export function joursDeRepos(pays = paysCourant) {
  return REPOS_PAR_PAYS[String(pays || '').toUpperCase()] || PAR_DEFAUT;
}

/**
 * @param {number} jourSemaine 0 = dimanche … 6 = samedi
 * @param {string|null} [pays] code du pays ; celui de l'entreprise par défaut
 * @returns {boolean} vrai si ce jour se travaille dans ce pays
 */
export function jourTravaille(jourSemaine, pays = paysCourant) {
  return !joursDeRepos(pays).includes(jourSemaine);
}

/**
 * Les jours de la semaine des langues que le navigateur ne connaît pas
 * (dimanche d'abord) : il les écrivait en anglais (« Mon »).
 */
const JOURS_SANS_NAVIGATEUR = {
  ny: ['Lamulungu', 'Lolemba', 'Lachiwiri', 'Lachitatu', 'Lachinayi', 'Lachisanu', 'Loweruka'],
  ss: ['Lisontfo', 'Umsombuluko', 'Lesibili', 'Lesitsatfu', 'Lesine', 'Lesihlanu', 'uMgcibelo'],
  ht: ['dim.', 'len.', 'mad.', 'mèk.', 'jed.', 'van.', 'sam.'],
  dv: ['އާދިއްތަ', 'ހޯމަ', 'އަންގާރަ', 'ބުދަ', 'ބުރާސްފަތި', 'ހުކުރު', 'ހޮނިހިރު'],
  sm: ['Aso Sā', 'Aso Gafua', 'Aso Lua', 'Aso Lulu', 'Aso Tofi', 'Aso Faraile', 'Aso Toʻonaʻi'],
};

/**
 * Le jour de la semaine en abrégé, dans la langue de l'écran : « lun. »,
 * « Mo. », « Mon »…
 *
 * @param {Date} date le jour
 * @param {string} langue code de la langue (« fr-CH », « de », « dv »…)
 * @returns {string} le nom du jour
 */
export function nomCourtDuJour(date, langue) {
  const base = String(langue || 'fr').split('-')[0];
  const propres = JOURS_SANS_NAVIGATEUR[base];
  if (propres) return propres[date.getDay()];
  return date.toLocaleDateString(langue || 'fr-CH', { weekday: 'short' });
}
